// PRIVATE API ROUTES

import {request, personalUrl, privateUrl, specialUrl} from "./common";

/**
 * Returns the current users document
 * Creates a new user document if userId nor email is found
 */
export async function getCurrentUser({accessToken}) {
  const url = personalUrl(`/users/currentuser`);
  return request({url, accessToken});
}

export async function getUsersPendingDogs({
  accessToken,
  options = {
    maxDepth: 4,
    populate: "sire dam owners breeder certificates.certificate organizations.organization",
  },
}) {
  if (!accessToken) return;
  const url = personalUrl(`/dogs/pending`);
  return request({url, accessToken, body: {options}});
}

export async function getUsersOwnedDogs({
  accessToken,
  options = {
    maxDepth: 4,
    populate: "sire dam owners breeder certificates.certificate organizations.organization",
  },
}) {
  if (!accessToken) return;
  const url = personalUrl(`/dogs/owned`);
  return request({url, accessToken, body: {options}});
}

export async function getUsersUnownedRecords({
  accessToken,
  options = {
    maxDepth: 4,
    populate: "sire dam owners breeder certificates.certificate organizations.organization",
  },
}) {
  if (!accessToken) return;
  const url = personalUrl(`/dogs/unownedrecords`);
  return request({url, accessToken, body: {options}});
}

export async function getUsersLitters({
  accessToken,
  options = {
    maxDepth: 4,
    populate: "sire dam puppies organizations.organization",
  },
}) {
  if (!accessToken) return;
  const url = personalUrl(`/litters/currentuser`);
  return request({url, accessToken, body: {options}});
}

export async function getUsersPendingLitters({
  accessToken,
  options = {
    maxDepth: 4,
    populate: "sire dam organizations.organization",
  },
}) {
  if (!accessToken) return;
  const url = personalUrl(`/litters/pending/currentuser`);
  return request({url, accessToken, body: {options}});
}

export async function getDogById(
  _id,
  {
    accessToken,
    options = {
      maxDepth: 4,
      populate: "sire dam owners dogOwners breeder certificates.certificate organizations.organization",
    },
  },
) {
  const url = privateUrl(`/dogs/byId/${_id}`);
  return request({url, accessToken, body: {options}});
}

// FDD-244: authenticated counterpart to publicRoutes' getDogByOrgRegNumber -- server-side
// route resolves regNumber -> _id itself now, so this is a single request, same as getDogById.
export async function getDogByOrgRegNumber(
  regNumber,
  {
    accessToken,
    options = {
      maxDepth: 4,
      populate: "sire dam owners dogOwners breeder certificates.certificate organizations.organization",
    },
  },
) {
  const url = privateUrl(`/dog/${regNumber}`);
  return request({url, accessToken, body: {options}});
}

export async function getDiffById(_id, {accessToken, options = {}} = {}) {
  const url = privateUrl(`/diffs/byId/${_id}`);
  try {
    return await request({url, accessToken, body: {options}});
  } catch (error) {
    console.error(`Error fetching diff by id: ${_id}`, error);
    throw new Error(`Unable to retrieve request by id: ${_id}`);
  }
}

export async function getPendingLitterById(
  _id,
  {
    accessToken,
    options = {
      maxDepth: 4,
      populate: {
        path: "puppies sire dam",
        populate: "organizations.organization",
      },
    },
  },
) {
  const url = privateUrl(`/litters/pending/byId/${_id}`);
  return request({url, accessToken, body: {options}});
}

export async function getLitterById(
  _id,
  {
    accessToken,
    options = {
      maxDepth: 4,
      populate: {
        path: "puppies sire dam",
        populate: "organizations.organization",
      },
    },
  },
) {
  const url = privateUrl(`/litters/byId/${_id}`);
  return request({url, accessToken, body: {options}});
}

export async function requestUpdateDog(_id, update, {accessToken, options}) {
  const url = privateUrl(`/dogs/byId/${_id}`);
  return request({
    url,
    method: "PATCH",
    accessToken,
    body: {update, options},
  });
}

export async function getDogsPendingDiffs(_id, {accessToken, options = {}}) {
  if (!accessToken || !_id) return;
  const url = privateUrl(`/dogs/byId/${_id}/diffs/pending`);
  return request({url, accessToken, body: {options}});
}

// Withdraw a diff entirely -- rejects it for every org it's still pending on
// in one server-side transaction, not just one. No organizationPrefix here:
// withdrawal isn't scoped to any single org, so there's nothing to pass.
export async function withdrawDiff(_id, {accessToken}) {
  const url = personalUrl(`/diffs/withdrawById/${_id}`);
  return request({url, method: "PATCH", accessToken});
}

export async function updateUser(update, {accessToken}) {
  const url = privateUrl(`/users/currentuser`);
  return request({url, method: "PATCH", accessToken, body: {update}});
}

export async function verifyUser(verifyUser, {accessToken}) {
  return updateUser({isVerified: verifyUser}, {accessToken});
}

export async function checkIfMicrochipExists(microchip, {accessToken}) {
  const url = specialUrl(`/microchipExists/${microchip}`);
  return request({url, method: "GET", accessToken});
}

export async function checkIfUserIsOwner(_id, {accessToken}) {
  const url = specialUrl(`/userOwnsDog/${_id}`);
  return request({url, method: "GET", accessToken});
}

export async function createLitter(document, {accessToken}) {
  const url = privateUrl(`/litters/new`);
  return request({url, accessToken, body: {document}});
}

export async function createDog(document, {accessToken, register=false, verifiedObject=false} = {}) {
  const url = privateUrl(`/dogs/new`);
  return request({url, accessToken, body: {document, register, verifiedObject}});
}

export async function createDogDiff(document, {accessToken}) {
  const url = privateUrl(`/dogs/new/diff`);
  return request({url, accessToken, body: {document}});
}

export async function rejectAndRevertDog(_id, {accessToken}) {
  const url = privateUrl(`/dogs/rejectAndRevert/${_id}`);
  return request({url, method: "POST", accessToken});
}

export async function markDiffAsError(_id, error, {accessToken}) {
  const url = privateUrl(`/diffs/error/${_id}`);
  return request({url, method: "PATCH", accessToken, body: {error}});
}

// Add profile picture to an existing dog
export async function updatePrimaryImageById(_id, fileName, {accessToken}) {
  const url = privateUrl(`/dogs/updatePrimaryImage/${_id}`);
  return request({url, method: "PATCH", accessToken, body: {fileName}});
}

export async function addAttachment(attachment, {accessToken}) {
  const url = privateUrl(`/attachments/new`);
  return request({url, accessToken, body: attachment});
}

export async function addTmpAttachment(attachment, {accessToken}, resize = false) {
  const url = privateUrl(`/attachments/tmp${resize ? "/resize" : ""}`);
  return request({url, accessToken, body: attachment});
}

export async function deleteTmpAttachment(link, {accessToken}) {
  const url = privateUrl(`/attachments/tmp/deleteByUrl`);
  return request({url, accessToken, body: {link}});
}

export async function deleteAttachment(attachment, {accessToken}) {
  // Attachment should be FormData
  const url = privateUrl(`/attachments/deleteById/${attachment.get("id")}`);
  return request({url, accessToken, method: "DELETE"});
}

export async function parseEmbarkPage(link, {accessToken}) {
  const body = {link};
  const url = privateUrl(`/parse/embark`);
  return request({url, accessToken, body});
}

export async function createStatistic(data, {accessToken}) {
  const url = privateUrl(`/statistics/new`);
  return request({
    url,
    accessToken,
    body: {document: {data, type: "litterRegistration"}},
  });
}

export async function createCheckout({accessToken, parentDoc, parentDocType, organizations}) {
  const url = privateUrl(`/orders/new`);
  return request({
    url,
    accessToken,
    body: {document: {parentDoc, parentDocType, organizations}},
  });
}
